import { type ClientLog, type Log, LogEnvironment, LogLevel } from '@domain/types';
import { isDesktopApp } from '@launcher/utils';
import electronService from '@services/electron/electron.service';
import { EnvManager } from '@services/environment/environment.service';
import envVariables from '@services/environment/variables';
import { monitoring } from '@services/monitoring';
import type { Logger as ElectronLog, FileTransport } from 'electron-log';
import { version } from '../../../package.json';
import { scrubFilePaths } from './scrubFilePaths';

type EnqueueLog = Pick<ClientLog, 'source' | 'message' | 'level'>;
type QueueLog = EnqueueLog & Pick<ClientLog, '@timestamp'>;

interface LoggerOptions {
  environment: LogEnvironment;
  version: Log['version'];
  beforeEnqueue(log: EnqueueLog): void;
}

type OptionsKey = Record<string, unknown>;

// Only these sources are batched and sent to the backend.
// Other sources still log to console / Sentry breadcrumbs but are never queued remotely.
const REMOTE_SOURCES = new Set<ClientLog['source']>(['ROUTER', 'HTTP']);

export class Logger {
  private static readonly MAX_BATCH_SIZE = 100;

  private consoleEnabled = false;

  private queue: QueueLog[] = [];

  private csrfToken = '';

  private flushTimer: ReturnType<typeof setInterval> | null = null;

  private desktopLogger: ElectronLog & { default: ElectronLog };

  private desktopLoggerInit = false;

  constructor(private readonly options: LoggerOptions) {
    this.shouldEnableFeatures(this.options.environment);
  }

  private shouldEnableFeatures(environment: LogEnvironment) {
    this.consoleEnabled =
      environment !== LogEnvironment.PRODUCTION && envVariables.NODE_ENV !== 'test';
  }

  private formatOptions(options: OptionsKey) {
    const formatedOptions: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(options)) {
      formatedOptions[key] =
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? value
          : JSON.stringify(value);
    }

    return formatedOptions;
  }

  private async writeToFile(log: EnqueueLog): Promise<void> {
    try {
      this.desktopLogger[log.level](log.message, JSON.stringify(log, null, '  '));
    } catch (err) {
      console.error(err);
    }
  }

  private addTimeStamp(log: EnqueueLog) {
    const now = new Date();
    return {
      ...log,
      '@timestamp': now.toISOString(),
    };
  }

  private enqueue(log: EnqueueLog): void {
    this.queue.push(this.addTimeStamp(log));
  }

  private log(log: EnqueueLog): void {
    const enriched = {
      ...log,
      path: window.location.pathname,
      version: this.options.version,
      environment: this.options.environment,
    };
    this.options.beforeEnqueue(enriched);
    if (REMOTE_SOURCES.has(enriched.source)) {
      this.enqueue(enriched);
    }

    if (this.consoleEnabled) {
      // biome-ignore lint/suspicious/noConsole: ok in the context of this logger
      console[enriched.level](enriched.message, JSON.stringify(enriched, null, '  '));
    }

    if (isDesktopApp() && this.desktopLoggerInit) {
      this.writeToFile(enriched);
    }
  }

  public async initDesktopLogger(): Promise<void> {
    if (!isDesktopApp() || !window.electron.getLogger) return;

    this.desktopLogger = window.electron.getLogger();
    this.disableConsoleTransport();
    await this.configureFileTransport();
    this.desktopLoggerInit = true;
  }

  private disableConsoleTransport(): void {
    if (this.desktopLogger.transports.console) {
      this.desktopLogger.transports.console.level = false; // disable console to avoid duplicate
    }
  }

  private async configureFileTransport(): Promise<void> {
    if (!this.desktopLogger.transports.file) return;

    try {
      const userDataPath = await electronService.getPath('userData');
      const logPath = await electronService.joinPaths(userDataPath, 'Logs', 'lifen_remote.log');

      (this.desktopLogger.transports.file as FileTransport).resolvePath = () => logPath;
    } catch (error) {
      console.error('Failed to configure file transport:', error);
      (this.desktopLogger.transports.file as FileTransport).resolvePath = () => '';
    }
  }

  public debug(
    source: ClientLog['source'],
    message: ClientLog['message'],
    options: OptionsKey = {},
  ): void {
    this.log({ source, message, level: LogLevel.DEBUG, ...this.formatOptions(options) });
  }

  public info(
    source: ClientLog['source'],
    message: ClientLog['message'],
    options: OptionsKey = {},
  ): void {
    this.log({ source, message, level: LogLevel.INFO, ...this.formatOptions(options) });
  }

  public warn(
    source: ClientLog['source'],
    message: ClientLog['message'],
    options: OptionsKey = {},
  ): void {
    this.log({ source, message, level: LogLevel.WARN, ...this.formatOptions(options) });
  }

  public setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  public startFlushing(): void {
    if (this.flushTimer) return;
    this.flushTimer = setInterval(() => this.flush(), 30_000);
    window.removeEventListener('beforeunload', this.flushSync);
    window.addEventListener('beforeunload', this.flushSync);
  }

  private sendBatch(batch: QueueLog[], keepalive = false): void {
    if (!this.csrfToken) return;
    fetch(`${EnvManager.getBaseUrlBackend()}/api/v1/client_logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        'X-Auth-Mode': 'session',
      },
      credentials: 'include',
      keepalive,
      body: JSON.stringify({ logs: batch }),
    }).catch(() => {});
  }

  private sendInBatches(entries: QueueLog[], keepalive = false): void {
    for (let i = 0; i < entries.length; i += Logger.MAX_BATCH_SIZE) {
      this.sendBatch(entries.slice(i, i + Logger.MAX_BATCH_SIZE), keepalive);
    }
  }

  private readonly flush = (): void => {
    if (this.queue.length === 0) return;
    this.sendInBatches(this.queue.splice(0));
  };

  private readonly flushSync = (): void => {
    if (this.queue.length === 0) return;
    this.sendInBatches(this.queue.splice(0), true);
  };

  public error(
    source: ClientLog['source'],
    message: ClientLog['message'],
    error: unknown = {},
    capture = true,
  ): void {
    this.log({
      source,
      message,
      level: LogLevel.ERROR,
      ...{
        error: scrubFilePaths(
          error instanceof Error ? error?.message : JSON.stringify(error as unknown),
        ),
      },
    });

    // Send errors to Sentry
    if (capture) {
      if (
        error instanceof Error ||
        (typeof error === 'object' && error !== null && Object.keys(error).length !== 0)
      ) {
        // Capture the inial error as a linked error to better group them https://docs.sentry.io/platforms/javascript/configuration/integrations/linkederrors/
        const sentryError = new Error(message);
        sentryError.cause = error;
        monitoring.addError(sentryError);
      } else {
        monitoring.addError(message);
      }
    }
  }
}

export const logger = new Logger({
  environment: EnvManager?.getName() as unknown as LogEnvironment, // FIXME: EnvManager is empty in some case ?
  version,
  beforeEnqueue: (log: EnqueueLog) => {
    monitoring.addEvent(log.source, log.message, log.level);
  },
});
